import React, { useState } from "react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * NotificationEnvelopeForm — Sub-sprint C.
 *
 * Minimal controlled form for `POST /api/admin/notification-envelopes`.
 * Surfaces the closed template registry and channel choices. Variables
 * are not editable inline in v0 (whitelisted keys with strict regex
 * checks server-side); the form sends an empty variables object.
 */
const TEMPLATE_KEYS = [
  "racketLab.email.ready_for_pickup",
  "racketLab.email.scheduled",
  "racketLab.email.cancelled",
  "racketLab.email.draft_received",
  "racketLab.email.sla_breach",
  "racketLab.internal.shift_force_closed",
  "racketLab.internal.ticket_reassigned",
];

const CHANNEL_FOR = (k) => (k.startsWith("racketLab.email.") ? "email" : "in_app");

export const NotificationEnvelopeForm = ({ value, onChange, disabled = false }) => {
  const t = useRacketLabAdminT();
  const [local, setLocal] = useState(
    value || {
      template_key: "racketLab.email.ready_for_pickup",
      channel: "email",
      recipient_user_id: "",
      language: "en",
      from_role: "shop",
      organisation_id: "TEAMSOFTWAREINNOVATIONS",
    },
  );
  const update = (k, v) => {
    const next = { ...local, [k]: v };
    if (k === "template_key") next.channel = CHANNEL_FOR(v);
    setLocal(next);
    onChange?.(next);
  };

  return (
    <div data-testid="rl-envelope-form" className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("notifications.envelopes.form.template_label")}</span>
        <select
          data-testid="rl-envelope-form-template"
          value={local.template_key}
          onChange={(e) => update("template_key", e.target.value)}
          disabled={disabled}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        >
          {TEMPLATE_KEYS.map((k) => (<option key={k} value={k}>{k}</option>))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("notifications.envelopes.form.channel_label")}</span>
        <input data-testid="rl-envelope-form-channel" value={local.channel} disabled className="rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-sm text-slate-500" />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("notifications.envelopes.form.recipient_label")}</span>
        <input
          data-testid="rl-envelope-form-recipient"
          value={local.recipient_user_id}
          onChange={(e) => update("recipient_user_id", e.target.value)}
          disabled={disabled}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
          placeholder={t("notifications.envelopes.form.recipient_placeholder")}
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-slate-700">{t("notifications.envelopes.form.language_label")}</span>
          <input
            data-testid="rl-envelope-form-language"
            value={local.language}
            onChange={(e) => update("language", e.target.value)}
            disabled={disabled}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-slate-700">{t("notifications.envelopes.form.from_role_label")}</span>
          <select
            data-testid="rl-envelope-form-from-role"
            value={local.from_role}
            onChange={(e) => update("from_role", e.target.value)}
            disabled={disabled}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="shop">shop</option>
            <option value="system">system</option>
            <option value="admin">admin</option>
          </select>
        </label>
      </div>

      <p className="text-[10px] text-slate-500">
        {t("notifications.envelopes.form.footer_note")}
      </p>
    </div>
  );
};

export default NotificationEnvelopeForm;
